/* import __COLOCATED_TEMPLATE__ from './create-discount-code-modal.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'ember-intl/services/intl';

interface Signature {
  Args: {
    isOpen: boolean;
    onModalClose: () => void;
    shopifyDomain: string | null;
    shopifyCustomerId?: number;
    currencySymbol?: string;
    conversationId: number;
    onDiscountCodeCreated?: () => void;
  };
}

export default class CreateDiscountCodeModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare session: $TSFixMe;

  @tracked code = '';
  @tracked title = '';
  @tracked discountType: 'percentage' | 'fixed_amount' = 'percentage';
  @tracked discountValue = '';
  @tracked usageLimit = '';
  @tracked endsAt = '';
  @tracked restrictToCustomer = true;
  @tracked isSuccessState = false;
  @tracked createdDiscountCode: string | null = null;

  get isCreateTaskRunning(): boolean {
    return taskFor(this.createDiscountCodeTask).isRunning;
  }

  get parsedDiscountValue(): number {
    return parseFloat(this.discountValue) || 0;
  }

  get canSubmit(): boolean {
    if (this.isCreateTaskRunning) {
      return false;
    }
    if (!this.code.trim() || !this.title.trim()) {
      return false;
    }
    if (this.parsedDiscountValue <= 0) {
      return false;
    }
    if (this.discountType === 'percentage' && this.parsedDiscountValue > 100) {
      return false;
    }
    return true;
  }

  get formattedDiscountValue(): string {
    if (this.discountType === 'percentage') {
      return `${this.parsedDiscountValue}%`;
    }
    let symbol = this.args.currencySymbol || '$';
    return `${symbol}${this.parsedDiscountValue}`;
  }

  get formattedUsageLimit(): string {
    return this.usageLimit
      ? this.usageLimit
      : this.intl.t(
          'inbox.conversation-details-sidebar.shopify.create-discount-code-modal.success-usage-limit-unlimited',
        );
  }

  get formattedExpiryDate(): string {
    if (!this.endsAt) {
      return this.intl.t(
        'inbox.conversation-details-sidebar.shopify.create-discount-code-modal.success-expiry-none',
      );
    }
    let [year, month, day] = this.endsAt.split('-');
    return `${day}/${month}/${year}`;
  }

  get todayDate(): string {
    return this.formatDate(new Date());
  }

  private formatDate(date: Date): string {
    let year = date.getFullYear();
    let month = String(date.getMonth() + 1).padStart(2, '0');
    let day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  @action
  handleModalClose() {
    if (this.isSuccessState) {
      this.args.onDiscountCodeCreated?.();
    }
    this.resetModalData();
    this.args.onModalClose();
  }

  @action
  onCodeChange(event: Event) {
    this.code = (event.target as HTMLInputElement).value;
  }

  @action
  onTitleChange(event: Event) {
    this.title = (event.target as HTMLInputElement).value;
  }

  @action
  onDiscountTypeChange(type: 'percentage' | 'fixed_amount') {
    this.discountType = type;
    this.discountValue = '';
  }

  @action
  onDiscountValueChange(event: Event) {
    this.discountValue = (event.target as HTMLInputElement).value;
  }

  @action
  onUsageLimitChange(event: Event) {
    this.usageLimit = (event.target as HTMLInputElement).value;
  }

  @action
  onEndsAtChange(event: Event) {
    this.endsAt = (event.target as HTMLInputElement).value;
  }

  @action
  toggleRestrictToCustomer() {
    this.restrictToCustomer = !this.restrictToCustomer;
  }

  private resetModalData() {
    this.code = '';
    this.title = '';
    this.discountType = 'percentage';
    this.discountValue = '';
    this.usageLimit = '';
    this.endsAt = '';
    this.restrictToCustomer = true;
    this.isSuccessState = false;
    this.createdDiscountCode = null;
  }

  @task({ restartable: true })
  *createDiscountCodeTask(): TaskGenerator<void> {
    try {
      let data: Record<string, unknown> = {
        app_id: this.session.workspace.id,
        shopify_domain: this.args.shopifyDomain,
        title: this.title,
        code: this.code,
        discount_type: this.discountType,
        discount_value: this.parsedDiscountValue,
        conversation_id: this.args.conversationId,
      };

      if (this.restrictToCustomer && this.args.shopifyCustomerId) {
        data['shopify_customer_id'] = this.args.shopifyCustomerId;
      }

      if (this.usageLimit) {
        data['usage_limit'] = parseInt(this.usageLimit, 10);
      }

      if (this.endsAt) {
        data['ends_at'] = this.endsAt;
      }

      let response: { success: boolean; discount_code: string } = yield ajax({
        url: '/ember/inbox/shopify/create_discount_code',
        type: 'POST',
        data: JSON.stringify(data),
      });

      this.createdDiscountCode = response.discount_code;
      this.isSuccessState = true;

      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'inbox.conversation-details-sidebar.shopify.create-discount-code-modal.success-title',
        ),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created_discount_code',
        object: 'shopify_discount_code',
        section: 'inbox_sidebar',
        shopify_domain: this.args.shopifyDomain,
        discount_type: this.discountType,
        restricted_to_customer: this.restrictToCustomer,
      });
    } catch (error: unknown) {
      captureException(error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t(
          'inbox.conversation-details-sidebar.shopify.create-discount-code-modal.errors.generic',
        ),
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify::CreateDiscountCodeModal': typeof CreateDiscountCodeModal;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify/create-discount-code-modal': typeof CreateDiscountCodeModal;
  }
}
